import React from 'react';
import { ColumnDefTemplate, CellContext, HeaderContext } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import Commission from '@/models/Commission';
import { getStatusGroup } from '@/helpers/requestStatus';
import payment from '@/assets/icons/payment.svg';
import donation from '@/assets/icons/donation.svg';
import support from '@/assets/icons/cell-support.svg';
import bankIcon from '@/assets/icons/bank.svg';
// import checkIcon from '@/assets/icons/check.svg';
// import cardIcon from '@/assets/icons/credit.svg';
import sortAsc from '@/assets/icons/sort-asc.svg';
import sortDesc from '@/assets/icons/sort-desc.svg';
import sortAscSelected from '@/assets/icons/sort-asc-select.svg';
import sortDescSelected from '@/assets/icons/sort-desc-select.svg';
import tooltipInfo from '@/assets/icons/tooltip-info.svg';
import alertIcon from '@/assets/icons/alert.svg';
import cellType from './CellTypes';
import { transactionTypeText } from './transactionTypeText';
import styles from './DataTable.module.scss';
import { parseTransactionDate, isPlausibleCivilDate, transactionCivilParts } from '@/helpers/dateHelper';
import DualCalendarDate from '@/components/ui/DualCalendarDate/DualCalendarDate';
import useCalendarMode from '@/hooks/useCalendarMode';
import CustomMenu from '../Menu';
import CustomTooltip from '../Tooltip/Tooltip';
import Checkbox from '@/components/ui/Checkbox/Checkbox';
import { PaymentMethodType } from '@/models/common/PaymentMethodType';

interface CellOptions {
  /**
   * MultiText only. The prefix repeats what the "transaction type" column already
   * shows, so denser presets drop it — but mobile hides that column and still needs it.
   */
  showKindPrefix?: boolean;
  /**
   * SupportedWithIsraeliID only. The id line under the name costs a second row,
   * so denser presets drop it — mobile keeps it because other columns are hidden.
   */
  showIsraeliId?: boolean;
  /**
   * Date only. Variant B dual Gregorian/Hebrew line (HAT-1210). Transactions
   * pass this so the desktop date column matches the phone rail; other boards
   * keep a single Gregorian date.
   */
  dualCalendar?: boolean;
}

function Cell<T>(type: cellType, options: CellOptions = {}): ColumnDefTemplate<CellContext<T, unknown>> {
  const { t, i18n } = useTranslation();
  const [calendarMode] = useCalendarMode();
  switch (type) {
    case cellType.Date: {
      return (cell: CellContext<T, unknown>) => {
        if (options.dualCalendar) {
          return <DualCalendarDate value={cell.getValue()} mode={calendarMode} missingLabel={t('missingTransactionDate')} />;
        }

        const date = parseTransactionDate(cell.getValue());
        if (!date) {
          return <span aria-label={t('missingTransactionDate')}>—</span>;
        }

        // A stored-but-implausible date is shown rather than hidden, so nobody reads an
        // empty cell as "nothing was entered" (SPR-1179). The year is padded to four
        // digits because he-IL prints year 15 as "15", which reads as 2015.
        if (!isPlausibleCivilDate(date)) {
          const { year, month, day } = transactionCivilParts(date);
          const padded = `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.${String(year).padStart(4, '0')}`;
          return (
            <span className={styles.implausibleDate} title={t('implausibleDateTooltip')}>
              {padded}
            </span>
          );
        }

        return (
          <span>
            {Intl.DateTimeFormat('he-IL', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            }).format(date)}
          </span>
        );
      };
    }

    case cellType.Status: {
      return (cell: CellContext<T, unknown>) => {
        const status = cell.getValue() as string;
        if (!status) return null;

        const group = getStatusGroup(status);

        return (
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-[10px] h-[10px] rounded-full shrink-0" style={{ backgroundColor: group?.iconColor || 'var(--icon-status-1)' }} />
            <span className={styles.cellText}>{t(group?.id || status)}</span>
          </div>
        );
      };
    }

    case cellType.PaymentMethod: {
      return (cell: CellContext<T, unknown>) => {
        const value = cell.getValue() as string;
        if (!value) return null;
        return <>{t(value)}</>;
      };
    }

    case cellType.PaymentWithType: {
      return (cell: CellContext<T, unknown>) => {
        const { method, isMonthly } = cell.getValue() as {
          method?: PaymentMethodType | null;
          isMonthly: boolean;
        };

        const getMethodIcon = () => {
          switch (method) {
            case PaymentMethodType.BankAccount:
              return <img src={bankIcon} alt="" className="w-4 h-4" />;
            // case PaymentMethodType.Check:
            //   return <img src={checkIcon} alt="" className="w-4 h-4" />;
            // case PaymentMethodType.CenterCard:
            //   return <img src={cardIcon} alt="" className="w-4 h-4" />;
            default:
              return <img src={bankIcon} alt="" className="w-4 h-4" />;
          }
        };

        const TranslatedValue = () => {
          const { t } = useTranslation();
          return (
            <div className="flex gap-2 items-center">
              {getMethodIcon()}
              <span>{isMonthly ? t('monthly') : t('oneTime')}</span>
            </div>
          );
        };

        return <TranslatedValue />;
      };
    }

    case cellType.MultiText: {
      const { showKindPrefix = true } = options;
      return (cell: CellContext<T, unknown>) => {
        const { kind, name } = cell.getValue() as {
          kind: string;
          name: string;
        };

        if (!showKindPrefix) {
          return (
            <span className={styles.cellText} style={{ fontWeight: 600 }}>
              {name}
            </span>
          );
        }

        let kindText = '';
        switch (kind) {
          case 'תרומה':
            kindText = t('donationFrom');
            break;
          case 'תשלום ספקים':
          case 'תשלום ספקים (הוראת קבע)':
            kindText = t('paymentTo');
            break;
          case 'תמיכה':
          case 'מלגה':
            kindText = t('supportFor');
            break;
        }

        return (
          <>
            <span>{kindText}:&nbsp;</span>
            <br />
            <span className={styles.cellText} style={{ fontWeight: 600, verticalAlign: 'bottom' }}>
              {name}
            </span>
          </>
        );
      };
    }

    case cellType.TransactionType: {
      return (cell: CellContext<T, unknown>) => {
        const transactionType = cell.getValue() as string;
        let typeStyle = {
          icon: undefined as string | undefined,
          color: 'text-(--font-primary)',
        };

        switch (transactionType) {
          case 'תרומה':
            typeStyle = { icon: donation, color: 'text-(--font-tertiary)' };
            break;
          case 'תשלום ספקים':
          case 'תשלום ספקים (הוראת קבע)':
            typeStyle = { icon: payment, color: 'text-(--font-primary)' };
            break;
          case 'תמיכה':
          case 'מלגה':
            typeStyle = { icon: support, color: 'text-(--font-primary)' };
            break;
        }
        return (
          <span className={`${typeStyle.color} flex gap-1`}>
            {transactionTypeText(transactionType, t)}
            <img src={typeStyle.icon} alt="" />
          </span>
        );
      };
    }

    case cellType.Receipt: {
      return (cell: CellContext<T, unknown>) => {
        const { kind, receiptId, onClick, future } = cell.getValue() as {
          kind: string;
          receiptId: number;
          onClick: () => void;
          /** HAT-1203: the movement has not happened, so "not issued yet" goes without saying. */
          future?: boolean;
        };
        if (!(kind === 'תרומה' || kind === 'פיקדון' || kind === 'הלוואה')) return null;
        if (!receiptId && future) return <span title={t('noReceipt')}>—</span>;
        if (!receiptId) return <>{t('noReceipt')}</>;
        return (
          <a
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            className={`text-(--font-link) ${styles.cellText}`}
            style={{ fontWeight: 600, textDecoration: 'underline' }}
          >
            {receiptId}
          </a>
        );
      };
    }

    case cellType.Notes: {
      return (cell: CellContext<T, unknown>) => {
        const monthlyCommission = cell.getValue() as Commission[];
        return (
          <>
            {monthlyCommission.map((commission, index) => (
              <div key={index}>
                {commission.category}, {t('count')}: {commission.count}, {t('amount')}: {commission.totalSum}
              </div>
            ))}
          </>
        );
      };
    }

    case cellType.Menu: {
      return (cell: CellContext<T, unknown>) => {
        const { items } = cell.getValue() as {
          items: { icon: string; text: string; onClick: () => void }[];
        };
        return <CustomMenu items={items} />;
      };
    }

    case cellType.Supported: {
      return (cell: CellContext<T, unknown>) => {
        const { firstName, lastName } = cell.getValue() as {
          firstName: string;
          lastName: string;
        };
        return (
          <a className={`text-(--font-link) ${styles.cellText}`} style={{ fontWeight: 600, textDecoration: 'underline' }}>
            {firstName} {lastName}
          </a>
        );
      };
    }

    case cellType.SupportedWithIsraeliID: {
      const { showIsraeliId = true } = options;
      return (cell: CellContext<T, unknown>) => {
        const { firstName, lastName, israeliId } = cell.getValue() as {
          firstName: string;
          lastName: string;
          israeliId: string;
        };
        if (!showIsraeliId) {
          return (
            <span className={styles.cellText} style={{ fontWeight: 600 }}>
              {firstName} {lastName}
            </span>
          );
        }
        return (
          <div>
            <div className={`${styles['double-row-upper']} ${styles.cellText}`}>
              {firstName} {lastName}
            </div>
            <span className={styles['double-row-lower']}>{israeliId}</span>
          </div>
        );
      };
    }

    case cellType.Number: {
      return (cell: CellContext<T, unknown>) => {
        const value = cell.getValue() as number;
        return <div>{value}</div>;
      };
    }

    case cellType.String: {
      return (cell: CellContext<T, unknown>) => {
        const value = cell.getValue() as string;
        return <div className={styles.cellText}>{value}</div>;
      };
    }

    case cellType.CommentWithRightIcon: {
      return (cell: CellContext<T, unknown>) => {
        const getComment = cell.getValue() as (() => string) | string;

        let comment: string;
        if (typeof getComment === 'function') {
          comment = getComment();
        } else {
          comment = getComment;
        }

        if (!comment) return null;

        return (
          <div className="flex items-center gap-2">
            <img src={alertIcon} alt="" className="w-4 h-4" />
            <span>{comment}</span>
          </div>
        );
      };
    }

    case cellType.Amount: {
      return (cell: CellContext<T, unknown>) => {
        const { amount, currency, ilsAmount, exchangeRate, fundCurrency, future, fractionDigits = 2 } = cell.getValue() as {
          amount: number;
          currency: string;
          /** Agorot shown; 0 where a narrow column has to fit a seven-figure balance (HAT-1396). */
          fractionDigits?: number;
          ilsAmount?: number;
          exchangeRate?: number;
          fundCurrency?: string;
          /**
           * HAT-1203: set on a row of «תנועות עתידיות». `alert` puts the red dot after the
           * amount with `alertText` in its tooltip; `caption` is the one-line note under it
           * («באיחור · אושר ל־…», «מותנה באישור ביצוע») for what the row cannot say by itself.
           */
          future?: { alert?: boolean; alertText?: string; caption?: string };
        };

        const formattedAmount = amount
          .toLocaleString(i18n.language, {
            style: 'currency',
            currency: currency || 'ILS',
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
          })
          .replace(/[\u200e\u200f\u061c]/g, '');

        return (
          <div className={`${styles.questionCircleContainer} ${future ? styles.futureAmountCell : ''}`}>
            <bdi
              dir="ltr"
              className={styles.amount}
              data-negative={amount < 0}
              data-in={amount > 0 || undefined}
              data-future={future ? 'true' : undefined}
              title={formattedAmount}
            >
              {formattedAmount}
            </bdi>
            {/* After the amount in DOM order, so in RTL the dot sits between the amount and the kind — where the design puts it. */}
            {future?.alert && (
              <>
                <span id={`futureAlert-${cell.row.id}`} className={styles.futureAlertDot} role="img" aria-label={future.alertText} />
                {future.alertText && (
                  <CustomTooltip targetId={`futureAlert-${cell.row.id}`}>
                    <p className="body-xs-sm-regular" style={{ whiteSpace: 'pre-line' }}>
                      {future.alertText}
                    </p>
                  </CustomTooltip>
                )}
              </>
            )}
            {future?.caption && <span className={styles.futureCaption}>{future.caption}</span>}
            {ilsAmount && exchangeRate && fundCurrency === 'ILS' && (
              <div>
                <img id={`usdAmountTooltip-${cell.row.id}`} src={tooltipInfo} alt="" width={16} />
                <CustomTooltip targetId={`usdAmountTooltip-${cell.row.id}`}>
                  <p className="body-xs-sm-regular">
                    {t('conversionAmountNote1')}{' '}
                    <span className="font-bold">
                      {'{'}
                      {amount.toLocaleString(i18n.language, {
                        style: 'currency',
                        currency: 'USD',
                      })}
                      {'} '}
                    </span>
                    <br />
                    {t('conversionAmountNote2')} {exchangeRate.toFixed(2)}
                    <br />
                    {t('conversionAmountNote3')}{' '}
                    <span className="font-bold">
                      {ilsAmount.toLocaleString(i18n.language, {
                        style: 'currency',
                        currency: 'ILS',
                      })}
                    </span>
                  </p>
                </CustomTooltip>
              </div>
            )}
          </div>
        );
      };
    }

    /*
     * HAT-1436: the fund's running balance after the row's movement - a secondary
     * figure next to the amount, not another amount, so it stays unbolded in the
     * muted tone CardList already uses for a non-definitive amount (amountMuted).
     * `value` is null on a row the server never computed one for - see
     * TransactionPage's accessorFn for exactly which rows that is - and a dash
     * says so rather than printing a balance of zero nobody actually had.
     */
    case cellType.Balance: {
      return (cell: CellContext<T, unknown>) => {
        const { value, currency, note } = cell.getValue() as { value: number | null; currency?: string; note?: string };

        // Guards the same way as `value === null`, in case a caller ever passes something
        // that isn't a real number - a missing field reads as undefined, not null.
        if (typeof value !== 'number' || Number.isNaN(value)) {
          // HAT-1448: a row older than the running balance says so in words, not a bare dash.
          if (note) {
            return (
              <span className={`${styles.cumulativeAmount} ${styles.cumulativeAmountNote}`} title={note}>
                {note}
              </span>
            );
          }
          return (
            <span className={styles.cumulativeAmount} title={t('cumulativeAmountUnavailable')}>
              —
            </span>
          );
        }

        const formatted = value
          .toLocaleString(i18n.language, {
            style: 'currency',
            currency: currency || 'ILS',
          })
          .replace(/[\u200e\u200f\u061c]/g, '');

        return (
          <bdi dir="ltr" className={styles.cumulativeAmount} title={formatted}>
            {formatted}
          </bdi>
        );
      };
    }

    case cellType.Selection: {
      return (cell: CellContext<T, unknown>) => {
        const row = cell.row;
        const table = cell.table;
        const meta = table.options.meta as
          | {
              lastSelectedRowIndexRef?: React.MutableRefObject<number | null>;
            }
          | undefined;

        const handleClick = (e: React.MouseEvent) => {
          e.stopPropagation();
          e.preventDefault();

          // Clear any text selection (happens on shift+click)
          window.getSelection()?.removeAllRanges();

          const currentIndex = row.index;

          if (e.shiftKey && meta?.lastSelectedRowIndexRef?.current !== null && meta?.lastSelectedRowIndexRef?.current !== undefined) {
            // Shift+Click: select range from last selected to current
            const lastIndex = meta.lastSelectedRowIndexRef.current;
            const [start, end] = lastIndex < currentIndex ? [lastIndex, currentIndex] : [currentIndex, lastIndex];

            const rows = table.getRowModel().rows;
            for (let i = start; i <= end; i++) {
              rows[i].toggleSelected(true);
            }
          } else {
            // Regular click: toggle single row
            row.toggleSelected(!row.getIsSelected());
          }

          // Update last selected index
          if (meta?.lastSelectedRowIndexRef) {
            meta.lastSelectedRowIndexRef.current = currentIndex;
          }
        };

        return (
          <div onClick={handleClick} className="flex items-center justify-center cursor-pointer h-full -mx-4 w-[calc(100%+2rem)]">
            <Checkbox checked={row.getIsSelected()} className="pointer-events-none" />
          </div>
        );
      };
    }

    case cellType.TruncatedList: {
      return (cell: CellContext<T, unknown>) => {
        const { items, tooltipId } = cell.getValue() as {
          items: (string | number)[];
          tooltipId: string;
        };

        if (!items || items.length === 0) return null;

        const MAX_VISIBLE = 3;

        if (items.length <= MAX_VISIBLE) {
          return <span>{items.join(', ')}</span>;
        }

        const visibleItems = items.slice(0, 2).join(', ');

        return (
          <div className="flex items-center gap-1">
            <span id={tooltipId} className="cursor-pointer">
              {visibleItems}...
            </span>
            <CustomTooltip targetId={tooltipId}>
              <span className="body-xs-sm-regular">{items.join(', ')}</span>
            </CustomTooltip>
          </div>
        );
      };
    }
  }
}

function Col<T>(title: string) {
  return (item: HeaderContext<T, unknown>) => {
    const canSort = item.column.getCanSort();

    if (!canSort) {
      return <span>{title}</span>;
    }
    const ascIcon = item.column.getIsSorted() === 'asc' ? sortAscSelected : sortAsc;
    const descIcon = item.column.getIsSorted() === 'desc' ? sortDescSelected : sortDesc;

    return (
      <button
        onClick={() => {
          switch (item.column.getIsSorted()) {
            case 'asc':
              item.column.toggleSorting(true, true);
              break;
            case 'desc':
              item.column.clearSorting();
              break;
            default:
              item.column.toggleSorting(false, true);
              break;
          }
        }}
        className={styles.columnHeaderButton}
        data-sorted={item.column.getIsSorted() || undefined}
      >
        <span>{title}</span>
        <div className={styles.sortIconsContainer}>
          <img src={ascIcon} alt="" />
          <img src={descIcon} alt="" className={'mt-[1.2px]'} />
        </div>
      </button>
    );
  };
}

export { Col, Cell };
